import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import productData from '../../Data/Products.json';
import FilterGroup from '../../Components/FilterGroup';
import { Helmet } from 'react-helmet-async';
const ProductPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stores, setStores] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState([]);
  const [selectedStore, setSelectedStore] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    const query = queryParams.get('query') || '';
    const page = parseInt(queryParams.get('page'), 10) || 1;

    const loadAndFilterProducts = () => {
      let allProducts = [];
      const categorySet = new Set();
      const storeSet = new Set();
      const subcategorySet = new Set();

      for (const category in productData) {
        for (const subcategory in productData[category]) {
          productData[category][subcategory].forEach(product => {
            allProducts.push({ ...product, category, subcategory });
          });
        }
      }

      const products = allProducts.filter(product =>
        product.name.toLowerCase().includes(query.toLowerCase())
      );

      products.forEach(product => {
        categorySet.add(product.category);
        subcategorySet.add(product.subcategory);
        storeSet.add(product.store);
      });

      setFilteredProducts(products);
      setCategories([...categorySet]);
      setSubcategories([...subcategorySet]);
      setStores([...storeSet]);
      setCurrentPage(page);
    };

    loadAndFilterProducts();
  }, [location.search]);

  useEffect(() => {
    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
      navigate(`?query=${queryParams.get('query') || ''}&page=${totalPages}`);
    }
  }, [filteredProducts, currentPage, itemsPerPage, navigate, queryParams]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(prev =>
      prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]
    );
    setCurrentPage(1);
    navigate(`?query=${queryParams.get('query') || ''}&page=1`);
  };

  const handleStoreChange = (store) => {
    setSelectedStore(prev =>
      prev.includes(store) ? prev.filter(s => s !== store) : [...prev, store]
    );
    setCurrentPage(1);
    navigate(`?query=${queryParams.get('query') || ''}&page=1`);
  };

  const handleSubcategoryChange = (subcategory) => {
    setSelectedSubcategory(prev =>
      prev.includes(subcategory) ? prev.filter(s => s !== subcategory) : [...prev, subcategory]
    );
    setCurrentPage(1);
    navigate(`?query=${queryParams.get('query') || ''}&page=1`);
  };

  const filteredByCategoryAndStore = filteredProducts.filter(product =>
    (selectedCategory.length === 0 || selectedCategory.includes(product.category)) &&
    (selectedStore.length === 0 || selectedStore.includes(product.store)) &&
    (selectedSubcategory.length === 0 || selectedSubcategory.includes(product.subcategory))
  );

  const indexOfLastProduct = currentPage * itemsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - itemsPerPage;
  const currentProducts = filteredByCategoryAndStore.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(filteredByCategoryAndStore.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    navigate(`?query=${queryParams.get('query') || ''}&page=${pageNumber}`);
  };

  const generatePaginationButtons = () => {
    const buttonRange = 2;
    const maxButtons = 5;
    const paginationButtons = [];

    let startPage, endPage;

    if (totalPages <= maxButtons) {
      startPage = 1;
      endPage = totalPages;
    } else {
      if (currentPage <= buttonRange + 1) {
        startPage = 1;
        endPage = maxButtons;
      } else if (currentPage + buttonRange >= totalPages) {
        startPage = totalPages - maxButtons + 1;
        endPage = totalPages;
      } else {
        startPage = currentPage - buttonRange;
        endPage = currentPage + buttonRange;
      }
    }

    paginationButtons.push(
      <button
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={currentPage === 1 ? 'disabled' : ''}
      >
        &lt;
      </button>
    );

    if (startPage > 1) {
      paginationButtons.push(
        <button
          key="1"
          onClick={() => handlePageChange(1)}
          className={currentPage === 1 ? 'active first-button' : 'first-button'}
        >
          1
        </button>
      );
      if (startPage > 2) {
        paginationButtons.push(
          <button
            key="left-ellipsis"
            onClick={() => handlePageChange(Math.max(1, currentPage - 5))}
          >
            ...
          </button>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      paginationButtons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={currentPage === i ? 'active' : ''}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        paginationButtons.push(
          <button
            key="right-ellipsis"
            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 5))}
          >
            ...
          </button>
        );
      }
      paginationButtons.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className={currentPage === totalPages ? 'active last-button' : 'last-button'}
        >
          {totalPages}
        </button>
      );
    }

    paginationButtons.push(
      <button
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={currentPage === totalPages ? 'disabled' : ''}
      >
        &gt;
      </button>
    );

    return paginationButtons;
  };

  // UseEffect to handle the reveal animation
  useEffect(() => {
    const productItems = document.querySelectorAll('.product-item');

    // Hide all products initially
    productItems.forEach(item => {
      item.classList.remove('visible');
      item.classList.add('hidden');
    });

    // Reveal each product with a slight delay between them for a staggered effect
    productItems.forEach((item, index) => {
      setTimeout(() => {
        item.classList.remove('hidden');
        item.classList.add('visible');
      }, 200); // Adjust the delay as needed (100ms here)
    });
  }, [currentProducts]);

  return (
    <>
    <Helmet>
      <title>Products - Affilizone</title>
      
      <meta name="robots" content="index, follow"></meta>
      <meta name="robots" content="INDEX, FOLLOW"></meta>
      <meta name="description" content="testing testing testing testing testing products"/>
      <link rel="canonical" href="https://affilizone-d16ba.web.app/products"/>
      
      <meta property="og:title" content="AffiliZone Philippines"/>
    <meta property="og:site_name" content="AffiliZone Philippines"/>
    <meta property="og:type" content="website"/>
    <meta property="og:url" content="https://affilizone-d16ba.web.app/"/>
    <meta property="og:image" content="https://affilizone-d16ba.web.app/logo512.png"/>
    <meta property="og:image:type" content="image/png"/>
    <meta property="og:image:height" content="512"/>
    <meta property="og:image:width" content="512"/>
    <meta property="og:description" content="Elevate Your Shopping with Curated Picks from AffiliZone"/>
    <meta name="twitter:card" content="summary_large_image"/>
    <meta name="twitter:title" content="Affilizone Philippines"/>
    <meta name="twitter:description" content="Elevate Your Shopping with Curated Picks from AffiliZone"/>
    <meta name="twitter:image" content="https://affilizone-d16ba.web.app/logo512.png/"/>
    </Helmet>
    <div className="affilizone-Content">
      <div className="product-page container">
        {filteredProducts.length === 0 ? (
          <div className="no-results">
            <h2>No results found</h2>
          </div>
        ) : (
          <div className="content-wrapper">
            <aside className="sidebar">
              <div className="filters">
              <h2>Filters</h2>
                <FilterGroup
                  title="Filter by Category"
                  items={categories}
                  selectedItems={selectedCategory}
                  onChange={handleCategoryChange}
                />
                <FilterGroup
                  title="Filter by Subcategory"
                  items={subcategories}
                  selectedItems={selectedSubcategory}
                  onChange={handleSubcategoryChange}
                />
                <FilterGroup
                  title="Filter by Store"
                  items={stores}
                  selectedItems={selectedStore}
                  onChange={handleStoreChange}
                />
              </div>
            </aside>

            <div className="results-pagination-wrapper">
              <div className="results">
                <div className="products">
                  {currentProducts.map((product, index) => (
                    <a
                      key={index}
                      href={product.link}
                      className="product-item"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img src={product.Image} alt={product.name} height={100}/>
                      <h2>{product.name}</h2>
                      <p>{product.store}</p>
                    </a>
                  ))}
                </div>
              </div>

              <div className="pagination">
                {generatePaginationButtons()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default ProductPage;
